/* istanbul ignore file */

import {
  type SessionWebPaymentAccount,
  type SessionWebResponse,
  type SessionWebStoneAccount,
  type SessionWebUser,
  type StoneAccountsResponse
} from '../entities'
import { entityFingerprint, personFingerprint, subjectFingerprint } from '../helpers/assertions'
import { type Entity, type OptIns, type PaymentAccount, type Person, type Subject } from '../types'

export const personBuilder = (overrides: Partial<Person> = {}): Person => ({
  [personFingerprint]: true,
  document: '63279091080',
  documentType: 'CPF',
  email: 'cleiton@finances.com.br',
  displayName: 'Cleiton',
  id: '4e30841b-5737-4f0d-afc5-590e5334d002',
  createdAt: '2022-11-29T20:21:32.225Z',
  approvalStatus: 'approved',
  roles: { acquirer: true, banking: true, credit: true },
  unsafe: {
    fullName: 'Cleiton da Silva Santos',
    legalName: 'Cleitinho'
  },
  ...overrides
})

export const paymentAccountBuilder = (overrides: Partial<PaymentAccount> = {}): PaymentAccount => ({
  accountCode: '123456',
  branchCode: '0001',
  id: '2d10a9b3-700c-4563-96d3-c63981907825',
  createdAt: '2022-11-28T20:21:32.225Z',
  ownerDocument: '46898533800',
  ownerId: 'c86bb14a-e5f3-4a21-a532-0fbf21ceb113',
  ownerName: 'Cleiton',
  status: 'ACTIVE',
  enabledFeatures: [],
  ownerKycStatus: 'approved',
  ...overrides
})

export const subjectBuilder = (overrides: Partial<Subject<OptIns>> = {}): Subject<OptIns> => ({
  [subjectFingerprint]: true,
  email: 'cleiton@finances.com.br',
  emailVerified: true,
  id: 'c86bb14a-e5f3-4a21-a532-0fbf21ceb113',
  phoneNumber: '+5511999999999',
  displayName: 'Cleiton Souza',
  address: {
    city: 'São Paulo',
    country: 'BR',
    neighborhood: 'Vila Olímpia',
    postalCode: '00000000',
    state: 'SP',
    street: 'Rua Faria Lima',
    streetNumber: '0000'
  },
  document: '46898533800',
  kycStatus: 'approved',
  approvalStatus: 'approved',
  organizations: [personBuilder()],
  users: [personBuilder()],
  kycRequest: undefined,
  paymentAccounts: [paymentAccountBuilder()],
  createdAt: '2022-11-29T20:21:32.225Z',
  credentials: [],
  unsafe: {
    fullName: 'Cleiton da Silva Santos',
    legalName: 'Cleitinho',
    hasAnActivePaymentAccount: true,
    roles: { banking: true, acquirer: true, credit: true },
    entitiesKycRequests: [],
    stoneCodes: []
  },
  enabledFeatures: [],
  ...overrides
})

export const entityBuilder = (overrides: Partial<Entity<OptIns>> = {}): Entity<OptIns> => ({
  [entityFingerprint]: true,
  ...personBuilder(),
  kycStatus: 'approved',
  paymentAccount: paymentAccountBuilder(),
  stoneCodes: [],
  hasAnActivePaymentAccount: true,
  enabledFeatures: [],
  rejectedKycChecks: [],
  stoneAccount: {
    paymentProfiles: [{ id: 'payment-profile-1' }, { id: 'payment-profile-2' }],
    id: '42',
    originBrand: 'stone',
    situation: 'legacy_affiliation'
  },
  ...overrides
})

export const deusExMachinaBuilder = (
  overrides: Partial<{ entity: Entity<OptIns>; subject: Subject<OptIns> }> = {}
): { entity: Entity<OptIns>; subject: Subject<OptIns> } => ({
  subject: subjectBuilder(),
  entity: entityBuilder(),
  ...overrides
})

export const sessionWebPaymentAccountBuilder = (
  overrides: Partial<SessionWebPaymentAccount> = {}
): SessionWebPaymentAccount => ({
  id: '6f0382d6-835f-4aad-ab93-2746c039c0c3',
  code: '10478626',
  branchCode: '1',
  status: 'ACTIVE',
  createdAt: '2021-01-12T14:29:45Z',
  featureFlags: ['barcode_payment_invoice'],
  ownerKycStatus: 'approved',
  ...overrides
})

export const sessionWebStoneAccountBuilder = (
  overrides: Partial<SessionWebStoneAccount> = {}
): SessionWebStoneAccount => ({
  id: 'e3015a51-24da-4f7d-8896-987480f0a962',
  legacy: {
    globalId: 'e3015a51-24da-4f7d-8896-987480f0a962'
  },
  name: 'Teste ADQTARIFASSSS',
  document: '75968672000194',
  documentType: 'CNPJ',
  email: 'conta@stone.com.br',
  originBrand: 'stone',
  situation: 'one_stone',
  createdAt: '2021-11-09T15:28:18.957Z',
  approvalStatus: 'approved',
  kyc: { status: 'approved', shouldRedirect: false },
  paymentProfiles: [{ id: 'payment-profile-1' }],
  featureFlags: ['payment_links'],
  actions: ['acquireraffiliation:read', 'paymentaccount:read', 'paymentaccount:loans'],
  kycRequest: {
    id: 'kyc-req-1',
    status: 'approved',
    reason: 'account_request',
    isMandatory: false,
    inputType: 'user_input',
    organizationId: 'a728ef9b-ceaf-456e-9419-215ac5bec68c',
    userId: 'c902d434-6d8c-4022-80fd-4ce61752e2ef',
    createdAt: '2023-06-02T19:14:05Z',
    updatedAt: '2023-06-02T19:14:05Z',
    checks: [],
    context: null
  },
  organizationsKycRequests: { data: [] },
  paymentAccount: sessionWebPaymentAccountBuilder(),
  ...overrides
})

export const sessionWebUserBuilder = (overrides: Partial<SessionWebUser> = {}): SessionWebUser => ({
  id: 'user:c902d434-6d8c-4022-80fd-4ce61752e2ef',
  email: 'gflandoli@stone.com.br',
  document: '39323694835',
  documentType: 'cpf',
  name: 'Giulianna Serva Colluci de Figueiredo Flandoli',
  phone: '11992864987',
  address: {
    street: 'Rua Urussui',
    streetNumber: '110',
    extra: '',
    referencePoint: '',
    neighborhood: 'Itaim Bibi',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '04542-050',
    country: 'Brasil'
  },
  actions: ['acquireraffiliation:read', 'paymentaccount:read', 'paymentaccount:loans'],
  featureFlags: ['payment_links'],
  credentials: ['pin', 'password'],
  createdAt: '2021-01-12T14:29:45Z',
  approvalStatus: 'approved',
  isOwner: true,
  ...overrides
})

export const sessionWebDataBuilder = (overrides: Partial<SessionWebResponse> = {}): SessionWebResponse => ({
  stoneAccount: sessionWebStoneAccountBuilder(),
  user: sessionWebUserBuilder(),
  ...overrides
})

export const stoneAccountsDataBuilder = (overrides: Partial<StoneAccountsResponse> = {}): StoneAccountsResponse => ({
  stoneAccounts: [sessionWebStoneAccountBuilder()],
  ...overrides
})

export const oneStoneDeusExMachinaBuilder = (
  overrides: Partial<{ entity: Entity<OptIns>; subject: Subject<OptIns> }> = {}
): { entity: Entity<OptIns>; subject: Subject<OptIns> } =>
  deusExMachinaBuilder({
    entity: entityBuilder({
      stoneAccount: {
        paymentProfiles: [{ id: 'payment-profile-1' }],
        id: 'e3015a51-24da-4f7d-8896-987480f0a962',
        originBrand: 'stone',
        situation: 'one_stone'
      }
    }),
    ...overrides
  })
